import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Clock,
  Users,
  Scale,
  ChefHat,
  Thermometer,
  Timer,
  AlertCircle,
  Download,
  Printer,
  Share2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { sopData } from '../../data/sopData';

const RecipeDetail = () => {
  const { id } = useParams();
  const recipe = sopData.recipes.find(r => r.id === id);

  if (!recipe) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">Recipe Not Found</h2>
        <p className="text-gray-500 mt-2">The recipe you're looking for doesn't exist.</p>
        <Link
          to="/sop/recipes"
          className="mt-6 text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Recipes
        </Link>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/sop/recipes"
          className="text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Recipes
        </Link>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{recipe.title}</h1>
            <p className="text-gray-500 mt-2">Version {recipe.version}</p>
          </div>
          <div className="flex gap-3">
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <Share2 className="w-5 h-5 text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <Printer className="w-5 h-5 text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <Download className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Recipe Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-3">
          <Timer className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-sm text-blue-600 font-medium">Prep Time</p>
            <p className="text-lg text-blue-900">{recipe.prepTime}</p>
          </div>
        </div>
        <div className="bg-green-50 rounded-xl p-4 flex items-center gap-3">
          <Scale className="w-5 h-5 text-green-600" />
          <div>
            <p className="text-sm text-green-600 font-medium">Yield</p>
            <p className="text-lg text-green-900">{recipe.yield}</p>
          </div>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 flex items-center gap-3">
          <ChefHat className="w-5 h-5 text-purple-600" />
          <div>
            <p className="text-sm text-purple-600 font-medium">Author</p>
            <p className="text-lg text-purple-900">{recipe.author}</p>
          </div>
        </div>
      </div>

      {/* Ingredients and Steps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-1">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Ingredients</h2>
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            {recipe.ingredients.map((ingredient, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-gray-700">{ingredient.item}</span>
                <span className="text-gray-500 font-medium">{ingredient.quantity}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Instructions</h2>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="space-y-6">
              {recipe.steps.map((step, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-medium">{step.step}</span>
                  </div>
                  <p className="text-gray-700 flex-1 pt-1">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      {recipe.notes && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Notes</h2>
          <div className="bg-amber-50 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <p className="text-amber-800">{recipe.notes}</p>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default RecipeDetail; 